export interface CnpjLookupResult {
  cnpj: string;
  razaoSocial: string;
  nomeFantasia: string | null;
  situacao: string | null;
}

export function normalizeCnpj(raw: string): string {
  return raw.replace(/\D/g, "").slice(0, 14);
}

export function formatCnpj(raw: string): string {
  const d = normalizeCnpj(raw);
  if (d.length <= 2) return d;
  if (d.length <= 5) return `${d.slice(0, 2)}.${d.slice(2)}`;
  if (d.length <= 8) return `${d.slice(0, 2)}.${d.slice(2, 5)}.${d.slice(5)}`;
  if (d.length <= 12) return `${d.slice(0, 2)}.${d.slice(2, 5)}.${d.slice(5, 8)}/${d.slice(8)}`;
  return `${d.slice(0, 2)}.${d.slice(2, 5)}.${d.slice(5, 8)}/${d.slice(8, 12)}-${d.slice(12)}`;
}

/**
 * Validates the CNPJ check digits (algoritmo módulo 11).
 * Returns false for sequences of equal digits (e.g. 00000000000000) and any
 * CNPJ that fails the standard check-digit calculation.
 */
export function isValidCnpj(raw: string): boolean {
  const cnpj = normalizeCnpj(raw);
  if (cnpj.length !== 14) return false;
  if (/^(\d)\1+$/.test(cnpj)) return false;

  const calcDigit = (slice: string, weights: number[]): number => {
    const sum = slice.split("").reduce((acc, n, i) => acc + parseInt(n, 10) * weights[i], 0);
    const mod = sum % 11;
    return mod < 2 ? 0 : 11 - mod;
  };

  const w1 = [5, 4, 3, 2, 9, 8, 7, 6, 5, 4, 3, 2];
  const w2 = [6, 5, 4, 3, 2, 9, 8, 7, 6, 5, 4, 3, 2];
  const d1 = calcDigit(cnpj.slice(0, 12), w1);
  const d2 = calcDigit(cnpj.slice(0, 12) + d1, w2);
  return d1 === parseInt(cnpj[12], 10) && d2 === parseInt(cnpj[13], 10);
}

interface BrasilApiCnpjResponse {
  cnpj?: string;
  razao_social?: string;
  nome_fantasia?: string;
  descricao_situacao_cadastral?: string;
  message?: string;
}

/**
 * Looks up a CNPJ on BrasilAPI (public, no auth required).
 * Returns null on 404 (CNPJ not found) and throws on network or other errors.
 */
export async function lookupCnpj(raw: string): Promise<CnpjLookupResult | null> {
  const cnpj = normalizeCnpj(raw);
  if (cnpj.length !== 14) return null;

  const response = await fetch(`https://brasilapi.com.br/api/cnpj/v1/${cnpj}`);
  if (response.status === 404) return null;
  if (!response.ok) throw new Error(`CNPJ lookup failed (${response.status})`);

  const data = (await response.json()) as BrasilApiCnpjResponse;
  if (!data.razao_social) return null;

  return {
    cnpj: data.cnpj ?? cnpj,
    razaoSocial: data.razao_social,
    nomeFantasia: data.nome_fantasia || null,
    situacao: data.descricao_situacao_cadastral || null,
  };
}
